import styles from "./addVariableModal.module.scss";
import { ModalWindow } from "../../../shared/components/modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import {
  array,
  object,
  string
} from "yup";
import {
  useFieldArray,
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "../../../shared/components/input/input";
import addBlockIcon from "../../../shared/images/addBlock.svg";
import removeBlockIcon from "../../../shared/images/removeBlock.svg";
import {
  useCallback,
  useMemo,
  useState
} from "react";
import { CreateVariableFirstStep } from "../../../types/variableType";
import { Environment } from "../../../types/environmentTypes";
import { getNonUniqueIndices } from "../../../utils/shared.utils";
import { useGetAllVariablesQuery } from "../../../api/variableService";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";

type AddVariableModalProps = {
  onClose: (newVariable?: CreateVariableFirstStep) => void;
  environments?: Array<Environment>;
}

export const AddVariableModal = ({ onClose, environments }: AddVariableModalProps) => {
  const { t } = useTranslation();

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const { data: variables } = useGetAllVariablesQuery("");

  const variablesNames: Array<string> = useMemo(() => {
    if (!!variables) {
      return variables.map(variable => variable.name);
    } else {
      return [];
    }
  }, [variables]);

  const schema = object({
    name: string().required(t("common.notValid")).max(128, t("common.notValid"))
      .test("unique", t("common.notUnique"), (value) => !variablesNames.includes(value)),
    values: array().required().of(
      object().shape({
        value: string().max(50000, t("common.notValid"))
      })
    ).test("unique", (value) => value.length === new Set(value.map(item => item.value))?.size)
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      values: [{ value: "" }]
    },
    mode: "onChange"
  });
  const { fields: valuesFields, append: appendValue, remove: removeValue } = useFieldArray({ control, name: "values" });
  const valuesWatch = useWatch({control, name: "values"});

  const getValuesErrorName = useCallback((index: number): string | undefined => {
    if (getNonUniqueIndices(valuesWatch, "value").includes(index)) {
      return t("common.notUnique");
    } else {
      return !!errors.values?.[index]?.value ? errors.values?.[index]?.value?.message : "";
    }
  }, [valuesWatch]);

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose();
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickNext = useCallback(() => {
    handleSubmit((result) => {
      onClose({
        name: result.name,
        values: result.values.map(item => ({ value: item.value || "" }))
      });
    })();
  }, [handleSubmit, onClose]);

  return (
    <>
      <ModalWindow
        title={t("variableManager.add")}
        onClose={onCancel}
        width={380}
        children={
          <div className={styles.container}>
            <div className={styles.item}>
              <div className={styles.label}>{t("common.name")}</div>
              <div className={styles.inputContainer}>
                <div className={styles.input}>
                  <Input control={control}
                         name={"name"}
                         error={!!errors.name ? errors.name.message : ""} />
                </div>
              </div>
            </div>
            <div className={styles.values}>
              <div className={styles.title}>
                {t("variableManager.values")}
                <img className={styles.addRemoveBlock}
                     onClick={() => appendValue({ value: "" })}
                     src={addBlockIcon}
                     alt=""/>
              </div>
              { valuesFields.map((valueField, index) => (
                <div className={styles.item}
                     key={valueField.id}>
                  <div className={styles.inputContainer}>
                    <div className={styles.input}>
                      <Input control={control}
                             name={`values.${index}.value`}
                             error={getValuesErrorName(index)}/>
                    </div>
                    { valuesFields.length !== 1 && (
                      <img className={styles.addRemoveBlock}
                           onClick={() => removeValue(index)}
                           src={removeBlockIcon} alt=""/>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.buttons}>
              <button className={styles.cancel}
                      onClick={onCancel}>{t("common.cancel")}</button>
              <button className={styles.next}
                      onClick={onClickNext}>{!environments?.length || valuesFields.length === 1 ? t("common.save") : t("variableManager.next")}</button>
            </div>
          </div>
        }
      />

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}